import useVisible from '@/hooks/useVisible';
import useWindowSize from '@/lib/hooks/useWindowSize';
import { useEffect, useRef, useState } from 'react';

const DEFAULT_AUTOPLAY_INTERVAL = 12;

/**
 *
 * @param itemsLength - the number of items in the carousel
 * @param autoplayInterval - the interval in seconds at which the carousel will advance automatically
 *
 * @returns activeFeatureIndex - the index of the active feature
 * @returns updateActiveFeatureIndex - a function to update the active feature index, used to advance the carousel manually
 * @returns isTimerActive - a boolean indicating if the timer is active
 * @returns setIsTimerActive - a function to set the timer active state, used to dismiss the timer when the user clicks on an item
 * @returns ref - the ref to the component which is used to monitor visibility
 *
 */
export const useCarouselTimer = (itemsLength: number, autoplayInterval: number = DEFAULT_AUTOPLAY_INTERVAL) => {
  const currentSlideTimeout = useRef<NodeJS.Timeout | null>(null);
  /*
   * Initial activeFeature index is null because we don't want to trigger
   * the progress bar of the first <CarouselTimelineItem> until its in view
   */
  const [activeFeatureIndex, setActiveFeatureIndex] = useState<number | null>(null);
  /*
   * Threshold indicates how much of the element must be visible to be considered seen.
   * Using 0.5 because the carousel content starts about halfway down the component.
   */
  const { hasSeen, ref } = useVisible({ threshold: 0.5 });
  const [isTimerActive, setIsTimerActive] = useState(true);

  // Convert seconds to milliseconds
  const msInterval = autoplayInterval * 1000;

  const updateActiveFeatureIndex = (newIndex: number) => setActiveFeatureIndex(newIndex);

  // Turn off the timer for mobile / if the window is less than 800px
  const { width } = useWindowSize();
  const isMobile = width && width < 800;

  // Check for isMobile changes
  useEffect(() => {
    if (isMobile) {
      setIsTimerActive(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (hasSeen && activeFeatureIndex === null) {
      updateActiveFeatureIndex(0);
    }
  }, [hasSeen]);

  useEffect(() => {
    // If activeFeatureIndex is null, the component is not in view yet
    if (activeFeatureIndex === null) return;

    // !isTimerActive indicates that the user clicked on an item - clear any pending timers and return
    if (!isTimerActive) {
      currentSlideTimeout.current && clearTimeout(currentSlideTimeout.current);
      return;
    }

    /*
     * If we reach this portion of the function, it means that the previous setTimeout
     * has incremented the activeFeatureIndex. Clear the old timeout and set a new one.
     */
    if (currentSlideTimeout.current) clearTimeout(currentSlideTimeout.current);

    const autoplay = () => {
      const isLastFeature = activeFeatureIndex >= itemsLength - 1;

      if (isLastFeature) updateActiveFeatureIndex(0);
      else updateActiveFeatureIndex(activeFeatureIndex + 1);
    };

    currentSlideTimeout.current = setTimeout(autoplay, msInterval);
  }, [activeFeatureIndex, isTimerActive, hasSeen]);

  return {
    activeFeatureIndex,
    updateActiveFeatureIndex,
    isTimerActive,
    setIsTimerActive,
    ref,
  };
};
